import { useIntl } from "react-intl";

import { FeaturedInfoCard } from "@/components/adg4/featured-info-card/src/featured-info-card";
import { IconSvgProps } from "@/libraries/wpl/foundations/src/icons/types";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { useLocalizedImage } from "../../use-localized-image";

import { FeaturedInfoSection, LAUNCH, FeaturedImages } from "./featured-info";

export const TimelineIcon = ({ size }: IconSvgProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M0 5.26807L16.5 5.26807V1.51807L0 1.51807V5.26807Z"
            fill="#1868DB"
        />
        <path
            d="M1.5 17.2681L18 17.2681V13.5181L1.5 13.5181L1.5 17.2681Z"
            fill="#1868DB"
        />
        <path
            d="M24 23.2681L7.5 23.2681V19.5181L24 19.5181V23.2681Z"
            fill="#1868DB"
        />
        <path
            d="M6 11.2681L22.5 11.2681V7.51806L6 7.51807V11.2681Z"
            fill="#1868DB"
        />
    </svg>
);

export const RedoIcon = ({ size }: IconSvgProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M2.87302 12.5736C2.87302 8.60683 6.08876 5.39109 10.0556 5.39109C14.0224 5.39109 17.2381 8.60683 17.2381 12.5736V13.415L15.3809 11.5578L13.3494 13.5893L18.6747 18.9146L24 13.5893L21.9685 11.5578L20.1111 13.4151V12.5736C20.1111 7.0201 15.6091 2.51807 10.0556 2.51807C4.50203 2.51807 0 7.0201 0 12.5736C0 18.1272 4.50203 22.6292 10.0556 22.6292V19.7562C6.08876 19.7562 2.87302 16.5405 2.87302 12.5736Z"
            fill="#1868DB"
        />
    </svg>
);

export const FastIcon = ({ size }: IconSvgProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M0 15.1847L10.6667 0.518066V9.8514H17.3333L6.66667 24.5181V15.1847H0Z"
            fill="#1868DB"
        />
    </svg>
);

export const LaunchSection = () => {
    const intl = useIntl();
    const showGwp7999JiraFeaturesUpdates = checkFeatureFlagGate(
        featureFlags.gwp7999JiraFeatures
    );

    const launchSharedReleaseDatesImage = useLocalizedImage(
        "launchSharedReleaseDatesImage"
    );
    const launchYourFavoriteAppImage = useLocalizedImage(
        "launchYourFavoriteAppImage"
    );
    const launchWorkflowsAutomationImage = useLocalizedImage(
        "launchWorkflowsAutomationImage"
    );

    if (showGwp7999JiraFeaturesUpdates) {
        return (
            <FeaturedInfoSection
                variant={LAUNCH}
                eyebrow={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Eyebrow",
                    defaultMessage: "Collaborate"
                })}
                header={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Header",
                    defaultMessage: "Get every team on the same page"
                })}
                description={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Description",
                    defaultMessage:
                        "Jira brings teams, tools, and AI together to keep the entire org in sync and deliver impact faster."
                })}
            >
                <FeaturedInfoCard
                    image={{
                        image: {
                            src: FeaturedImages.collaborateStreamlineRequests,
                            alt: intl.formatMessage({
                                id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Card.StreamlineRequests.Alt",
                                defaultMessage: "Forms in Jira"
                            })
                        }
                    }}
                    Icon={TimelineIcon}
                    title={intl.formatMessage({
                        id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Card.StreamlineRequests.Header",
                        defaultMessage: "Streamline Requests"
                    })}
                    body={intl.formatMessage({
                        id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Card.StreamlineRequests.Description",
                        defaultMessage:
                            "Make it easy for partner teams to submit requests and track progress with forms."
                    })}
                />
                <FeaturedInfoCard
                    image={{
                        image: {
                            src: FeaturedImages.collaborateShareKeyResources,
                            alt: intl.formatMessage({
                                id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Card.ShareResources.Alt",
                                defaultMessage: "Release management in Jira"
                            })
                        }
                    }}
                    Icon={RedoIcon}
                    title={intl.formatMessage({
                        id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Card.ShareResources.Header",
                        defaultMessage: "Share key resources"
                    })}
                    body={intl.formatMessage({
                        id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Card.ShareResources.Description",
                        defaultMessage:
                            "Keep everyone aligned by sharing key resources like content calendars or release announcements."
                    })}
                />
                <FeaturedInfoCard
                    image={{
                        image: {
                            src: FeaturedImages.collaborateAutomateWorkflows,
                            alt: intl.formatMessage({
                                id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Card.AutomateWorkflows.Alt",
                                defaultMessage: "Manage workflows in Jira"
                            })
                        }
                    }}
                    Icon={FastIcon}
                    title={intl.formatMessage({
                        id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Card.AutomateWorkflows.Header",
                        defaultMessage: "Automate workflows"
                    })}
                    body={intl.formatMessage({
                        id: "JiraSoftwareFeaturesPageV2.Gwp7999LaunchSection.Card.AutomateWorkflows.Description",
                        defaultMessage:
                            "Scale processes and save time with customizable workflows that integrate with your tech stack."
                    })}
                />
            </FeaturedInfoSection>
        );
    }

    return (
        <FeaturedInfoSection
            variant={LAUNCH}
            eyebrow={intl.formatMessage({
                id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Eyebrow",
                defaultMessage: "Collaborate and launch"
            })}
            header={intl.formatMessage({
                id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Header",
                defaultMessage: "Connect, collaborate, celebrate"
            })}
            description={intl.formatMessage({
                id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Description",
                defaultMessage:
                    "Jira better connects teams and work for more seamless collaboration and faster alignment."
            })}
        >
            <FeaturedInfoCard
                image={{
                    image: {
                        src: launchSharedReleaseDatesImage,
                        alt: intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Card.ReleaseDates.ImageAlt",
                            defaultMessage: "Shared release date module in Jira"
                        })
                    }
                }}
                Icon={TimelineIcon}
                title={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Card.ReleaseDates.Header",
                    defaultMessage: "Shared release dates"
                })}
                body={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Card.ReleaseDates.Description",
                    defaultMessage:
                        "The only place where teams who make your product are connected to teams who launch it. When dates change, every team is in the know."
                })}
            />
            <FeaturedInfoCard
                image={{
                    image: {
                        src: launchYourFavoriteAppImage,
                        alt: intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Card.SlackFigma.ImageAlt",
                            defaultMessage: "Slack and Jira integration"
                        })
                    }
                }}
                Icon={RedoIcon}
                title={intl.formatMessage(
                    {
                        id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Card.SlackFigma.Header",
                        defaultMessage:
                            "Slack, Figma, {openBracket}your favorite app{closeBracket} "
                    },
                    {
                        openBracket: "<",
                        closeBracket: ">"
                    }
                )}
                body={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Card.SlackFigma.Description",
                    defaultMessage:
                        "Bring in real-time data and information from popular 3rd party apps like Slack, Figma, and GitHub, so Jira becomes your team’s trusted source for information."
                })}
            />
            <FeaturedInfoCard
                image={{
                    image: {
                        src: launchWorkflowsAutomationImage,
                        alt: intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Card.WorkflowAutomation.ImageAlt",
                            defaultMessage:
                                "Workflow trigger setup screen in Jira"
                        })
                    }
                }}
                Icon={FastIcon}
                title={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Card.WorkflowsAutomation.Header",
                    defaultMessage: "Workflows and automation"
                })}
                body={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightLaunchSection.Card.WorkflowAutomation.Description",
                    defaultMessage:
                        "Save time and scale your work confidently with no-code automation and workflows, like approvals, that keeps work moving forward when no one is watching."
                })}
            />
        </FeaturedInfoSection>
    );
};
